import { cacheKeys } from "@constants/cache-keys";
import { buildType } from "@helpers/expo-extras";
import { isWeb } from "@helpers/platform";
import { useAppStore } from "@hooks/useAppStore";
import { isDev } from "@memorang/constants/src/urls";
import Box from "@memorang/ui/src/components/Box";
import Button from "@memorang/ui/src/components/Button";
import { useQuery } from "@tanstack/react-query";
import Constants from "expo-constants";
import { type ReactNode, createContext, useContext, useState } from "react";
import { Card, Text } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";

type TenantsVersionData = {
	[tenantKey: string]: string;
};

type VersionCheckerContextType = {
	isNewVersionAvailable: boolean;
	currentVersion: string;
	remoteVersion?: string;
	dismissBanner: () => void;
	refreshApp: () => void;
	isDismissed: boolean;
};

const VersionCheckerContext = createContext<VersionCheckerContextType | null>(
	null,
);

export const useVersionChecker = () => {
	const context = useContext(VersionCheckerContext);
	if (!context) {
		throw new Error(
			"useVersionChecker must be used within VersionCheckerProvider",
		);
	}
	return context;
};

type VersionCheckerProviderProps = {
	children: ReactNode;
};

const RefreshBanner = () => {
	const { dismissBanner, refreshApp } = useVersionChecker();
	const insets = useSafeAreaInsets();

	return (
		<Box
			position="fixed"
			bottom={0}
			right={0}
			zIndex={1000}
			paddingRight={16}
			paddingBottom={Math.max(insets.bottom, 16)}
			backgroundColor="transparent"
			// @ts-ignore
			role="banner"
		>
			<Card elevation={4}>
				<Card.Content
					style={{
						display: "flex",
						flexDirection: "column",
						gap: 16,
					}}
				>
					<Text variant="bodyMedium">
						New version available, please click refresh to update.
					</Text>

					<Box
						flexDirection="row"
						alignItems="center"
						gap={8}
						justifyContent="flex-end"
					>
						<Button
							onPress={dismissBanner}
							accessibilityLabel="Dismiss update notification"
						>
							Dismiss
						</Button>
						<Button
							mode="contained"
							onPress={refreshApp}
							accessibilityLabel="Refresh to update app"
						>
							Refresh
						</Button>
					</Box>
				</Card.Content>
			</Card>
		</Box>
	);
};

const VersionCheckerProvider = ({ children }: VersionCheckerProviderProps) => {
	const [isDismissed, setIsDismissed] = useState(false);
	const currentVersion = Constants.expoConfig?.version || "0.0.0";

	const baseUrl = useAppStore((state) => state.app.links.baseUrl);

	const { data: remoteVersionData } = useQuery<TenantsVersionData>({
		queryKey: [cacheKeys.versionChecker, currentVersion],
		queryFn: async () => {
			const response = await fetch(`${baseUrl}/version.json`, {
				cache: "no-store",
			});

			if (!response.ok) {
				return null;
			}

			const data = await response.json();

			return data || null;
		},
		enabled: isWeb && !!buildType,
		staleTime: 0,
		gcTime: 0,
		refetchOnWindowFocus: true,
		refetchOnReconnect: true,
		refetchOnMount: true,
		refetchInterval: 60000,
		retry: false,
	});

	const remoteVersion = remoteVersionData?.[buildType];

	const isNewVersionAvailable = !!(
		isWeb &&
		remoteVersion &&
		remoteVersion !== currentVersion &&
		!isDismissed &&
		!isDev
	);

	const dismissBanner = () => {
		setIsDismissed(true);
	};

	const refreshApp = () => {
		window.location.reload();
	};

	const contextValue: VersionCheckerContextType = {
		isNewVersionAvailable,
		currentVersion,
		remoteVersion,
		dismissBanner,
		refreshApp,
		isDismissed,
	};

	return (
		<VersionCheckerContext.Provider value={contextValue}>
			{children}
			{isNewVersionAvailable && <RefreshBanner />}
		</VersionCheckerContext.Provider>
	);
};

export default VersionCheckerProvider;
